// src/features/users/roles.ts
//
// Which roles «Usuarios» offers and what each needs. Same rules as the API
// (UsersController: CanAssignRole, hotel/group assignment per role).
import type { UserRole } from './types';

/** Every role, highest privilege first. */
export const ALL_ROLES: UserRole[] = ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'];

/** Roles the current user may give: SuperAdmin all (SuperAdmin itself only for staff managers),
 *  Admin all but SuperAdmin, GroupAdmin only lower ranks. */
export const getAssignableRoles = (currentRole: UserRole | undefined, canManageSuperAdmins: boolean): UserRole[] => {
  switch (currentRole) {
    case 'SuperAdmin':
      return canManageSuperAdmins ? ALL_ROLES : ALL_ROLES.filter((r) => r !== 'SuperAdmin');
    case 'Admin':
      return ALL_ROLES.filter((r) => r !== 'SuperAdmin');
    case 'GroupAdmin':
      return ['HotelAdmin', 'Receptionist', 'Viewer'];
    default:
      return [];
  }
};

/** Roles shown in the list's role filter. */
export const getVisibleRoles = (currentRole: UserRole | undefined): UserRole[] => {
  switch (currentRole) {
    case 'SuperAdmin':
      return ALL_ROLES;
    case 'Admin':
      return ALL_ROLES.filter((r) => r !== 'SuperAdmin');
    case 'GroupAdmin':
      return ['GroupAdmin', 'HotelAdmin', 'Receptionist', 'Viewer'];
    case 'HotelAdmin':
      return ['HotelAdmin', 'Receptionist', 'Viewer'];
    default:
      return [];
  }
};

/** Roles scoped to hotels: at least one hotel must be assigned. */
export const roleNeedsHotels = (role: string | undefined): boolean =>
  role === 'HotelAdmin' || role === 'Receptionist' || role === 'Viewer';

/** Roles scoped to chains: at least one chain must be assigned. */
export const roleNeedsGroups = (role: string | undefined): boolean => role === 'GroupAdmin';

/** i18n key of a role name (resources.users.roles.*). */
export const roleLabelKey = (role: string): string =>
  `resources.users.roles.${role.charAt(0).toLowerCase() + role.slice(1)}`;
